import { isEmpty, isEqual } from 'lodash';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { FakeLink } from '@hivebrite/design-system/Link';
import { Loader } from '@hivebrite/design-system/Loader';
import { usePrevious } from '@hivebrite/react-hooks';

import { useCommentsClient } from 'hb-react/frontoffice/apps/CommentsV2/hooks/commentsClient';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';

import { SimpleComment } from './SimpleComment';
import { orderCommentsByAsc, getLastCommentsAsc } from '../utils';

const MAX_DEFAULT_REPLIES = 2;

const Replies = ({
  canDisplayEmojiPicker,
  canPaginateComments,
  commentableId,
  commentableType,
  currentUser,
  isOpenInModal,
  onDeleteReply,
  onReply,
  onToggleLike,
  onUpdateReplies,
  onUpdateReply,
  replies: initialReplies,
}) => {
  const queryClient = useQueryClient();

  const [isExpanded, setIsExpanded] = useState(false);
  const [replies, setReplies] = useState([]);
  const [totalReplies, setTotalReplies] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [displayedReplies, setDisplayedReplies] = useState([]);

  const prevInitialReplies = usePrevious(initialReplies);

  const { getComments } = useCommentsClient();

  const infiniteQueryKey = useMemo(
    () => [
      'paginated-replies',
      commentableType,
      commentableId,
      isOpenInModal ? 'modal' : 'page',
    ],
    [commentableType, commentableId, isOpenInModal],
  );

  // We paginate the replies
  const {
    isFetching: isFetchingFromInfiniteQuery,
    hasNextPage,
    data: dataFromInfiniteQuery,
    fetchNextPage,
  } = useInfiniteQuery(
    infiniteQueryKey,
    ({ pageParam = 1 }) =>
      getComments({
        commentableId,
        commentableType,
        page: pageParam,
        perPage: 5,
      }),
    {
      getNextPageParam: ({ _metadata: { page, has_next_page: hasNext } }) =>
        hasNext ? page + 1 : undefined,
      onError: (error) => {
        if (error?.response?.status === 403) {
          displayXhrError(error);
        }
      },
      enabled: canPaginateComments,
    },
  );

  const displayAllReplies = useCallback(() => {
    setIsExpanded(true);
  }, [setIsExpanded]);

  const displayLessReplies = useCallback(() => {
    setIsExpanded(false);
  }, [setIsExpanded]);

  const loadMoreReplies = useCallback(() => {
    if (fetchNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage]);

  useEffect(() => {
    if (
      !isEqual(prevInitialReplies, initialReplies) &&
      initialReplies.items &&
      initialReplies.items.length > 0
    ) {
      setReplies(initialReplies.items);
      setTotalReplies(initialReplies.total);
    }
  }, [prevInitialReplies, initialReplies]);

  useEffect(() => {
    if (!isFetchingFromInfiniteQuery && !isEmpty(dataFromInfiniteQuery)) {
      const { pages = [] } = dataFromInfiniteQuery;

      const loadedCurrentPage = pages.length;

      if (loadedCurrentPage !== currentPage && pages[currentPage]) {
        const { replies: loadedReplies, _metadata } = pages[currentPage];

        if (loadedReplies.length > 0) {
          const newItems = loadedReplies.filter(
            (item) =>
              replies.find((itemOriginal) => itemOriginal.id === item.id) ===
              undefined,
          );

          const newReplies = [...replies, ...newItems];

          setReplies(newReplies);
          setTotalReplies(_metadata.total_items);
          setCurrentPage(currentPage + 1);

          onUpdateReplies({
            replies: newReplies,
            totalReplies: _metadata.total_items,
          });
        }
      }
    }
  }, [
    isFetchingFromInfiniteQuery,
    dataFromInfiniteQuery,
    replies,
    currentPage,
  ]);

  useEffect(() => {
    if (totalReplies > 0) {
      if (canPaginateComments) {
        setDisplayedReplies(orderCommentsByAsc(replies));
      } else if (isExpanded) {
        setDisplayedReplies(orderCommentsByAsc(replies));
      } else {
        setDisplayedReplies(
          getLastCommentsAsc(orderCommentsByAsc(replies), MAX_DEFAULT_REPLIES),
        );
      }
    }
  }, [canPaginateComments, isExpanded, replies, totalReplies]);

  useEffect(
    () => () => {
      if (canPaginateComments) {
        queryClient.setQueryData(infiniteQueryKey, () => ({}));
      }
    },
    [],
  );

  if (isFetchingFromInfiniteQuery && totalReplies === 0) {
    return (
      <Box py={6}>
        <Loader size={15} />
      </Box>
    );
  }

  return (
    <Box height="100%" width="100%" pl={6} flexDirection="column">
      {canPaginateComments ? (
        <Box alignSelf="flex-start" mb={3}>
          {hasNextPage &&
            (isFetchingFromInfiniteQuery ? (
              <>
                <Box mr={1} font="body-semibold-asphalt">
                  {s__('Replies|Loading...')}
                </Box>
                <Loader size={15} />
              </>
            ) : (
              <FakeLink onClick={loadMoreReplies}>
                {s__('Replies|See more replies')}
              </FakeLink>
            ))}
        </Box>
      ) : (
        totalReplies > MAX_DEFAULT_REPLIES && (
          <Box mb={3}>
            {displayedReplies.length !== totalReplies ? (
              <FakeLink onClick={displayAllReplies}>
                {__('Load all replies')}
              </FakeLink>
            ) : (
              <FakeLink onClick={displayLessReplies}>
                {__('See less replies')}
              </FakeLink>
            )}
          </Box>
        )
      )}

      {totalReplies > 0 && (
        <RepliesWrapper>
          {displayedReplies.map((reply, index) => (
            <Box
              pb={index === displayedReplies.length - 1 ? 0 : 3}
              data-testid="comment-reply"
              key={reply.id}
            >
              <SimpleComment
                canDisplayEmojiPicker={canDisplayEmojiPicker}
                comment={reply}
                currentUser={currentUser}
                isReply
                onDeleteComment={onDeleteReply}
                onReply={onReply}
                onToggleLike={onToggleLike}
                onUpdateComment={onUpdateReply}
              />
            </Box>
          ))}
        </RepliesWrapper>
      )}
    </Box>
  );
};

export { Replies };

const RepliesWrapper = styled(Box)`
  flex-direction: column;
  width: 100%;
  position: relative;
  margin-bottom: 8px;
  &:before {
    content: '';
    position: absolute;
    left: -24px;
    width: 1px;
    height: 100%;
    background: #cecece;
    border-radius: 1px;
  }
`;
